import { Controller } from "@hotwired/stimulus"
import Chart from 'chart.js/auto'
import annotationPlugin from 'chartjs-plugin-annotation'

Chart.register(annotationPlugin)

export default class extends Controller {
  static targets = [
    "totalAscent", 
    "totalDescent", 
    "chartContainer"
  ]

  static values = {
    poiTraceId: String,
    apiUrl: String
  }
  
  connect() {
    console.log('Elevation Profile Chart Controller connected')
    console.log('Poi Trace ID:', this.poiTraceIdValue)
    console.log('API URL:', this.apiUrlValue)
    
    this.fetchElevationData()
  }

  async fetchElevationData() {
    try {
      const fullUrl = `${window.location.origin}${this.apiUrlValue}`
      console.log('Fetching from URL:', fullUrl)
      
      const response = await fetch(fullUrl)
      const data = await response.json()
      console.log('Données brutes reçues:', data)
      console.log('Structure des points:', data.points.map(p => ({
        id: p.id,
        distance: p.distance,
        elevation: p.elevation
      })))
      
      // Mise à jour des statistiques
      this.totalAscentTarget.textContent = `+${data.metadata.total_ascent} m`
      this.totalDescentTarget.textContent = `-${data.metadata.total_descent} m`

      // Dessiner le graphique
      this.drawChart(data)
    } catch (error) {
      console.error('Error fetching elevation data:', error)
    }
  }

  drawChart(data) {
    // Préparer les données
    const points = data.points
    const startDistance = points[0].distance
    const endDistance = points[points.length - 1].distance
    const distances = points.map(point => point.distance)
    const elevations = points.map(point => point.elevation)
    
    // Trouver le point zéro
    const zeroPoint = points.find(point => point.distance === 0)
    console.log('Point zéro:', zeroPoint)
    
    // Créer le canvas
    const canvas = document.createElement('canvas')
    canvas.style.width = '100%'
    canvas.style.height = '100%'
    this.chartContainerTarget.innerHTML = ''
    this.chartContainerTarget.appendChild(canvas)
    
    // Configuration du graphique
    const ctx = canvas.getContext('2d')
    const chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: distances.map(d => d / 1000), // Convertir en km
        datasets: [
          ...data.major_stops.map(stop => ({
            label: stop.name,
            data: [{
              x: stop.distance / 1000,
              y: stop.elevation
            }],
            borderColor: '#1e3a8a',
            backgroundColor: '#fab313',
            pointRadius: 6,
            pointHoverRadius: 8,
            pointBorderWidth: 2,
            showLine: false,
            zIndex: 1000
          })),
          {
            label: 'Élévation',
            data: elevations,
            borderColor: '#fab313',
            backgroundColor: (context) => {
              const ctx = context.chart.ctx;
              const gradient = ctx.createLinearGradient(0, 0, 0, 400);
              gradient.addColorStop(0, 'rgba(30, 58, 138, 0.4)');
              gradient.addColorStop(0.5, 'rgba(30, 58, 138, 0.2)');
              gradient.addColorStop(1, 'rgba(30, 58, 138, 0)');
              return gradient;
            },
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            zIndex: 0
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 20,
            right: 20,
            bottom: 20,
            left: 20
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                return `Altitude: ${context.parsed.y}m`;
              },
              title: function(context) {
                return `Distance: ${context[0].parsed.x}km`;
              }
            },
            backgroundColor: 'rgba(30, 58, 138, 0.9)',
            titleColor: 'white',
            bodyColor: 'white',
            padding: 10,
            displayColors: false
          },
          annotation: {
            annotations: data.major_stops.map(stop => ({
              type: 'label',
              xValue: stop.distance / 1000,
              yValue: stop.elevation,
              content: stop.name,
              backgroundColor: 'white',
              borderColor: '#1e3a8a',
              borderWidth: 1,
              color: '#1e3a8a',
              font: {
                size: 14,
                weight: 'normal'
              },
              padding: {
                top: 4,
                right: 8,
                bottom: 4,
                left: 8
              },
              position: 'top',
              yAdjust: -25,
              borderRadius: 8
            }))
          }
        },
        scales: {
          x: {
            type: 'linear',
            reverse: true,
            display: true,
            grid: {
              display: true,
              borderDash: [5, 5],
              color: 'rgba(30, 58, 138, 0.1)'
            },
            ticks: {
              display: true,
              stepSize: 2,
              autoSkip: false,
              callback: function(value) {
                if (value === 0) {
                  return '0 km'
                }
                if (value === startDistance / 1000 || value === endDistance / 1000) {
                  return ''
                }
                return `${value} km`
              }
            },
            min: startDistance / 1000,
            max: endDistance / 1000,
            position: 'bottom'
          },
          y: {
            display: true,
            grid: {
              display: true,
              borderDash: [5, 5],
              color: 'rgba(30, 58, 138, 0.1)'
            },
            ticks: {
              display: true,
              stepSize: 50,
              callback: function(value) {
                return `${Math.round(value / 50) * 50}m`
              }
            },
            min: Math.floor(Math.min(...elevations) / 50) * 50,
            max: Math.ceil(Math.max(...elevations) / 50) * 50,
            position: 'left'
          },
          y2: {
            display: true,
            grid: {
              display: false
            },
            ticks: {
              display: true,
              stepSize: 50,
              callback: function(value) {
                return `${Math.round(value / 50) * 50}m`
              }
            },
            min: Math.floor(Math.min(...elevations) / 50) * 50,
            max: Math.ceil(Math.max(...elevations) / 50) * 50,
            position: 'right'
          }
        }
      }
    })

    // Dessiner les labels après que le graphique soit rendu
    chart.update()
    setTimeout(() => {
      const ctx = canvas.getContext('2d')
      ctx.font = 'bold 14px sans-serif'
      ctx.textAlign = 'center'
      
      data.major_stops.forEach(stop => {
        const stopDistance = stop.distance
        const stopElevation = stop.elevation
        
        // Calculer la position du label
        const x = chart.scales.x.getPixelForValue(stopDistance / 1000)
        const y = chart.scales.y.getPixelForValue(stopElevation) - 20
        
        console.log(`Dessin du label pour ${stop.name} à (${x}, ${y})`)
        
        // Dessiner le fond blanc avec une bordure
        const textWidth = ctx.measureText(stop.name).width
        const padding = 8
        const height = 24
        
        // Fond blanc
        ctx.fillStyle = 'white'
        ctx.fillRect(x - textWidth/2 - padding, y - height/2, textWidth + padding*2, height)
        
        // Bordure
        ctx.strokeStyle = '#1e3a8a'
        ctx.lineWidth = 1
        ctx.strokeRect(x - textWidth/2 - padding, y - height/2, textWidth + padding*2, height)
        
        // Dessiner le texte
        ctx.fillStyle = '#1e3a8a'
        ctx.fillText(stop.name, x, y)
      })
    }, 100)

    // Animer le défilement vers la droite
    const scrollContainer = this.chartContainerTarget.closest('.overflow-x-auto')
    if (scrollContainer) {
      // Forcer la visibilité de la barre de défilement
      scrollContainer.style.overflowX = 'scroll'
      
      // Ajouter une transition CSS pour un défilement plus doux
      scrollContainer.style.scrollBehavior = 'smooth'
      scrollContainer.style.transition = 'scroll 1.5s ease-in-out'
      
      // Styliser la barre de défilement
      scrollContainer.style.scrollbarColor = '#fab313 transparent'
      scrollContainer.style.scrollbarWidth = 'thin'
      
      // Attendre que le graphique soit rendu
      setTimeout(() => {
        scrollContainer.scrollTo({
          left: scrollContainer.scrollWidth,
          behavior: 'smooth'
        })
      }, 300)
    }
  }
} 